import {App as AntdApp, Alert, Modal, Popconfirm, Button as AntdButton, Tag} from "antd";
import dayjs from "dayjs";
import {FontAwesomeIcon} from "~/components";

const nf = (n) => Number(n || 0).toLocaleString('vi-VN');

/** 1 dòng thông tin chuyển khoản + nút copy giá trị. */
function InfoRow({label, value, copyable, notification}) {
	if (!value) return null;

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(String(value));
			notification.success({message: 'Đã sao chép', description: String(value)});
		} catch (e) {
			// Trình duyệt chặn clipboard (http) — user tự bôi đen copy.
		}
	};

	return (
		<div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '7px 0', borderBottom: '1px dashed #eef1f4'}}>
			<span style={{color: '#6b7280', fontSize: 13, flexShrink: 0}}>{label}</span>
			<span style={{fontWeight: 600, textAlign: 'right', wordBreak: 'break-all'}}>
				{value}
				{copyable && (
					<AntdButton type="text" size="small" onClick={copy} style={{marginLeft: 4}}
						icon={<FontAwesomeIcon icon="fa-light fa-copy" />} />
				)}
			</span>
		</div>
	);
}

/**
 * Màn thanh toán đơn hàng gói (đăng ký/nâng cấp/gia hạn) — KHÔNG có cổng thanh toán:
 * hiện QR VietQR (số tiền + nội dung CK = mã đơn duy nhất) → khách chuyển khoản xong bấm
 * "Tôi đã thanh toán" (báo admin đối soát) → admin duyệt ở Quản lý thành viên là gói
 * kích hoạt/chu kỳ mới bắt đầu. `order` = payload từ api/subscription/upgrade-request.
 */
function PaymentModal({open, order, planName, confirming, cancelling, onConfirmPaid, onCancelOrder, onClose}) {
	const {notification} = AntdApp.useApp();

	if (!order) return null;

	const isRenew = order.kind === 'renew';
	// Đơn 'downgrade' = thanh toán gói ĐÍCH của lịch hạ gói (tick tạo trước 3 ngày hết hạn).
	const isDowngrade = order.kind === 'downgrade';
	const paid = Boolean(order.paid_at);
	const kindTag = isDowngrade
		? {color: 'orange', label: 'Hạ gói'}
		: isRenew ? {color: 'blue', label: 'Gia hạn'} : {color: 'purple', label: 'Nâng cấp'};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			width={480}
			title={
				<span>
					<FontAwesomeIcon icon="fa-light fa-qrcode" />{' '}
					{isDowngrade ? 'Thanh toán chuyển gói' : isRenew ? 'Thanh toán gia hạn gói' : 'Thanh toán đăng ký gói'}
				</span>
			}
			footer={[
				<Popconfirm
					key="cancel"
					title="Huỷ đơn hàng này?"
					description={isRenew || isDowngrade ? 'Huỷ đơn — hệ thống sẽ không nhắc lại; quá hạn gói trở về Miễn phí.' : 'Bạn có thể tạo đơn mới bất cứ lúc nào.'}
					okText="Huỷ đơn"
					cancelText="Thôi"
					onConfirm={onCancelOrder}
				>
					<AntdButton danger loading={cancelling}>Huỷ đơn</AntdButton>
				</Popconfirm>,
				<AntdButton
					key="paid"
					type="primary"
					loading={confirming}
					disabled={paid}
					onClick={onConfirmPaid}
					icon={<FontAwesomeIcon icon="fa-light fa-circle-check" />}
				>
					{paid ? 'Đã báo thanh toán' : 'Tôi đã thanh toán'}
				</AntdButton>,
			]}
		>
			<div style={{textAlign: 'center', marginBottom: 8}}>
				<div style={{fontSize: 15}}>
					{isDowngrade ? 'Chuyển sang gói ' : isRenew ? 'Gia hạn gói ' : 'Đăng ký gói '}
					<b>{planName || order.plan_slug}</b>
					{' '}<Tag color={kindTag.color}>{kindTag.label}</Tag>
				</div>
				<div style={{fontSize: 26, fontWeight: 700, color: '#dc2626', margin: '4px 0'}}>{nf(order.amount)}đ</div>
			</div>

			{order.qr_url ? (
				<div style={{textAlign: 'center', marginBottom: 10}}>
					<img src={order.qr_url} alt="QR thanh toán VietQR" style={{width: 260, maxWidth: '100%', borderRadius: 12, border: '1px solid #eef1f4'}} />
					<div style={{color: '#6b7280', fontSize: 13, marginTop: 6}}>
						Quét QR bằng app ngân hàng — số tiền và nội dung chuyển khoản đã điền sẵn.
					</div>
				</div>
			) : (
				<Alert
					type="info" showIcon style={{marginBottom: 10}}
					message="Chưa có mã QR thanh toán"
					description="Hệ thống chưa cấu hình tài khoản nhận tiền. Vui lòng liên hệ quản trị viên để được hướng dẫn thanh toán, kèm mã đơn bên dưới."
				/>
			)}

			<div style={{background: '#f9fafb', borderRadius: 10, padding: '4px 14px', marginBottom: 10}}>
				{order.bank && <InfoRow label="Ngân hàng" value={order.bank.bank_id} notification={notification} />}
				{order.bank && <InfoRow label="Số tài khoản" value={order.bank.account_no} copyable notification={notification} />}
				{order.bank && <InfoRow label="Chủ tài khoản" value={order.bank.account_name} notification={notification} />}
				<InfoRow label="Số tiền" value={`${nf(order.amount)}đ`} notification={notification} />
				<InfoRow label="Nội dung CK (bắt buộc)" value={order.code} copyable notification={notification} />
			</div>

			{paid ? (
				<Alert
					type="success" showIcon
					message={`Bạn đã báo thanh toán lúc ${dayjs(order.paid_at).format('HH:mm DD/MM/YYYY')}`}
					description={isRenew
						? 'Quản trị viên đối soát xong sẽ gia hạn gói — chu kỳ mới bắt đầu, hạn mức làm mới.'
						: isDowngrade
							? 'Quản trị viên đối soát xong sẽ chuyển bạn sang gói mới — chu kỳ mới bắt đầu.'
							: 'Quản trị viên đối soát xong sẽ kích hoạt gói cho bạn.'}
				/>
			) : (
				<Alert
					type="warning" showIcon
					message="Sau khi chuyển khoản, hãy bấm “Tôi đã thanh toán”"
					description="Hệ thống không tự nhận biết giao dịch — nút này báo cho quản trị viên vào đối soát và kích hoạt gói."
				/>
			)}
		</Modal>
	);
}

export default PaymentModal;
